/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  TemplateRef,
  inject,
  input,
  output,
  viewChild,
  contentChild,
} from '@angular/core';
import { Subject, filter, fromEvent, takeUntil, throttleTime } from 'rxjs';
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'cmg-split-button',
  templateUrl: './cmg-split-button.component.html',
  styleUrls: ['./cmg-split-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
})
export class CmgSplitButtonComponent implements OnDestroy {
  private readonly _elementRef = inject(ElementRef);
  private readonly _changeDetectorRef = inject(ChangeDetectorRef);

  readonly panelRef = viewChild<ElementRef>('panel');

  readonly optionTemplate = contentChild<TemplateRef<any>>('optionTemplate');

  readonly menuItems = input<any[]>();

  @HostBinding('class.disabled')
  @Input()
  disabled = false;

  readonly selectionChanged = output<any>();
  isShowingMenu = false;
  private readonly _destroyed$ = new Subject<boolean>();
  private _monitor$: Subject<boolean>;

  ngOnDestroy(): void {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  activateMenu() {
    if (!this.disabled) {
      this._showMenu();
    }
  }

  closeMenu() {
    if (!this.disabled) {
      this.panelRef().nativeElement.style.display = 'none';
      this._elementRef.nativeElement.appendChild(this.panelRef().nativeElement);
      this.isShowingMenu = false;
      if (this._monitor$) {
        this._monitor$?.next(false);
        this._monitor$?.complete();
      }
      this._monitor$ = null;
      this._changeDetectorRef.detectChanges();
    }
  }

  // I show the pop-up menu, and try to position it so it doesn't overlay with the
  // viewport of the browser.
  private _showMenu(): void {
    const panelRef = this.panelRef();
    document.body.appendChild(panelRef.nativeElement);
    const rootRect = this._elementRef.nativeElement.getBoundingClientRect();

    // By default, we're going to position the menu at the top-left corner of the
    // root button.

    panelRef.nativeElement.style.left = `${rootRect.left - this._calculateLeftOffset(rootRect)}px`;
    panelRef.nativeElement.style.top = `${rootRect.top + rootRect.height + 4}px`;
    panelRef.nativeElement.style.width = null;
    panelRef.nativeElement.style.minWidth = `${rootRect.width}px`;
    panelRef.nativeElement.style.height = null;

    // Since we don't know what's inside the menu (the content is projected), there's
    // no way for us to know about the dimensions ahead of time. As such, we're going
    // to stop and force the browser to reconcile the view-model with the template
    // (ie, we're going to force it to render the menu). This will give the menu
    // physical dimensions in the viewport that we can then measure.
    panelRef.nativeElement.style.height = 'auto';
    panelRef.nativeElement.style.position = 'absolute';
    panelRef.nativeElement.style.display = 'flex';
    this.isShowingMenu = true;
    this._changeDetectorRef.detectChanges();
    this._monitor$ = new Subject<boolean>();
    setTimeout(() => {
      // setTimeout here avoids quick open/close.
      this._setupMonitoring();
    }, 500);
  }

  private _setupMonitoring() {
    if (!this._monitor$) {
      this._monitor$ = new Subject<boolean>();
    }
    fromEvent(document, 'mousedown')
      .pipe(
        throttleTime(100),
        filter((t) => this._isOutsideSplitButton(t.target)),
        takeUntil(this._monitor$),
      )
      .subscribe(() => {
        this.closeMenu();
      });
    fromEvent(document, 'mouseup')
      .pipe(
        throttleTime(100),
        filter((t) => this._isOutsideSplitButton(t.target)),
        takeUntil(this._monitor$),
      )
      .subscribe(() => {
        this.closeMenu();
      });
    fromEvent(document, 'mousemove')
      .pipe(
        throttleTime(100),
        filter((t) => this._isOutsideSplitButton(t.target)),
        takeUntil(this._monitor$),
      )
      .subscribe(() => {
        this.closeMenu();
      });
    fromEvent(window, 'resize')
      .pipe(takeUntil(this._monitor$))
      .subscribe(() => {
        this.closeMenu();
      });
    fromEvent(document, 'scroll')
      .pipe(takeUntil(this._monitor$))
      .subscribe(() => {
        this.closeMenu();
      });
  }

  private readonly _isOutsideSplitButton = (target: EventTarget): boolean => {
    return !this.panelRef().nativeElement.contains(target) && !this._elementRef.nativeElement.contains(target);
  };

  /** Provides offset for the `left` position value, so that dropdown is viewable for smaller browser widths. */
  private _calculateLeftOffset(domRect: DOMRect) {
    return window.innerWidth < 700 ? domRect.width + (window.innerWidth > 575 ? 40 : 0) : 0;
  }
}
